/**
 * 网络层错误的统一识别、文案与重试判定。
 *
 * 为什么需要它：响应拦截器原来的 error 分支只是把 error 原样 reject，于是
 * 「请求超时」「断网」「DNS / 连接失败」「服务端 5xx」和「业务校验不通过」
 * 在调用方眼里完全一样 —— 用户在弱网下拿不到任何可操作的反馈，各个页面只能
 * 自己在 catch 里猜一句「加载失败」，文案还彼此不一致。
 *
 * 这里把判定收在一处，输出规范化结果，供拦截器决定：提示什么、要不要重试。
 */

/**
 * 超时分级。
 *
 * 原来是全局 36000（看着像把 3600ms 误写成 36000ms）—— 弱网下用户要干等 36 秒
 * 才知道失败，等于没有超时。按请求性质分档：
 * - READ  读请求。超过这个时间用户早已认为「卡住了」，不如尽快给出可重试的反馈。
 * - WRITE 写请求。重发有副作用（重复发帖 / 重复点赞），宁可多等也要让它有机会走完。
 * - AUTH  刷新 token。必须比 READ 更短：它是所有 401 请求的公共前置，
 *         挂住它等于挂住整条队列，要第一时间失败掉走重新登录。
 */
export const TIMEOUT = {
  READ: 15000,
  WRITE: 25000,
  AUTH: 10000
}

/** 幂等方法 —— 只有这些方法上的失败才可以安全重试 */
const IDEMPOTENT_METHODS = ['get', 'head', 'options']

/** 该 HTTP 方法重发是否无副作用 */
export function isIdempotentMethod (method) {
  return IDEMPOTENT_METHODS.indexOf(String(method || 'get').toLowerCase()) !== -1
}

/** 网络层失败最多额外重试的次数 */
const MAX_RETRY = 2

export const ERROR_KIND = {
  /** 明确处于断网状态 */
  OFFLINE: 'offline',
  /** 请求超时 */
  TIMEOUT: 'timeout',
  /** 连接建立/传输失败（DNS、连接被拒、传输中断） */
  NETWORK: 'network',
  /** 服务端 5xx */
  SERVER: 'server',
  /** 请求被主动取消（切页面、搜索词变化） */
  CANCELED: 'canceled',
  /** 认证 / 封禁哨兵错误，提示已由对应弹窗承担 */
  SILENT: 'silent',
  /** 业务或客户端错误，不属于弱网范畴 */
  BUSINESS: 'business'
}

const MESSAGES = {
  [ERROR_KIND.OFFLINE]: '网络已断开，请检查网络连接',
  [ERROR_KIND.TIMEOUT]: '网络连接超时，请稍后重试',
  [ERROR_KIND.NETWORK]: '网络连接失败，请检查网络后重试',
  [ERROR_KIND.SERVER]: '服务器繁忙，请稍后重试'
}

/** 这类失败的成因在网络/服务端，而不是这次请求本身不对 */
const NETWORK_KINDS = [
  ERROR_KIND.OFFLINE,
  ERROR_KIND.TIMEOUT,
  ERROR_KIND.NETWORK,
  ERROR_KIND.SERVER
]

export function isNetworkKind (kind) {
  return NETWORK_KINDS.indexOf(kind) !== -1
}

/** 认证 / 封禁哨兵值，命中后一律静默 */
const SILENT_SENTINELS = ['__SILENT_AUTH__', '__ACCOUNT_BANNED__']

/**
 * 判断当前是否确定处于断网状态。
 *
 * navigator.onLine 只有 false 是可信的（true 仅表示有网卡链路，不代表能出网），
 * 所以这里只用它把「确定断网」从「连不上」里区分出来，用于给出更准确的文案。
 */
function isDefinitelyOffline () {
  return typeof navigator !== 'undefined' && navigator.onLine === false
}

/**
 * 把一个 axios 错误归类成 ERROR_KIND 之一
 *
 * @param {Error} error axios 抛出的错误
 * @returns {{ kind: String, message: String, retryable: Boolean }}
 */
export function classifyRequestError (error) {
  const kind = detectKind(error)
  return {
    kind,
    message: MESSAGES[kind] || '',
    // 只有「快速失败」的网络问题才值得自动重试，详见 shouldRetry 的注释
    retryable: kind === ERROR_KIND.NETWORK || kind === ERROR_KIND.SERVER,
    /**
     * 失败成因在网络/服务端，而不是这次请求（或本地凭证）本身有问题。
     * 用于区分「这次没成功，等会儿还能成」和「服务端明确拒绝了」——
     * 前者绝不能拿去做清登录态这类破坏性处置。
     */
    isRecoverable: isNetworkKind(kind)
  }
}

function detectKind (error) {
  if (!error) return ERROR_KIND.BUSINESS

  if (SILENT_SENTINELS.indexOf(error.message) !== -1) {
    return ERROR_KIND.SILENT
  }

  // axios 1.x 取消请求为 ERR_CANCELED；搜索页用的旧式 CancelToken 会带 __CANCEL__
  if (error.code === 'ERR_CANCELED' || error.__CANCEL__) {
    return ERROR_KIND.CANCELED
  }

  // axios 把超时报成 ECONNABORTED（部分环境是 ETIMEDOUT），
  // 且 message 形如 "timeout of 15000ms exceeded"
  if (
    error.code === 'ECONNABORTED' ||
    error.code === 'ETIMEDOUT' ||
    /timeout/i.test(error.message || '')
  ) {
    return ERROR_KIND.TIMEOUT
  }

  const status = error.response && error.response.status
  if (status) {
    // 5xx 是服务端问题，重试有意义；4xx 是这次请求本身不对，重试只是白跑
    return status >= 500 ? ERROR_KIND.SERVER : ERROR_KIND.BUSINESS
  }

  // 走到这里说明请求根本没拿到响应
  if (error.code === 'ERR_NETWORK' || error.request) {
    return isDefinitelyOffline() ? ERROR_KIND.OFFLINE : ERROR_KIND.NETWORK
  }

  return ERROR_KIND.BUSINESS
}

/**
 * 是否应该自动重试这次失败。
 *
 * 刻意**不重试超时**：一次 READ 超时已经花掉 15 秒，再重试两次就是 45 秒，
 * 比不重试更糟。超时应该交给用户决定要不要重来（页面上的重试入口），
 * 自动重试只用在 ERR_NETWORK、5xx 这类**快速失败**上 —— 它们通常一秒内就返回，
 * 退避后重试一次往往就过了，代价很小。
 *
 * 也刻意不重试确定断网的情况：立刻告诉用户「没网」比先耗两轮退避再说更有用。
 *
 * 调用方可以用 config.retry 显式覆盖（0 = 关闭重试）。
 *
 * @param {Object} config axios 请求配置
 * @param {{ retryable: Boolean }} classified classifyRequestError 的结果
 */
export function shouldRetry (config, classified) {
  if (!config || !classified.retryable) return false

  const limit = typeof config.retry === 'number' ? config.retry : MAX_RETRY
  if (limit <= 0) return false

  // 非幂等方法自动重发可能造成重复发帖 / 重复扣减，一律不重试
  if (!isIdempotentMethod(config.method)) return false

  return (config.__retryCount || 0) < limit
}

/**
 * 第 n 次重试前的等待时长：指数退避 + 随机抖动。
 *
 * 抖动是必须的：弱网恢复的瞬间，页面上几十个失败请求会在同一毫秒一起重试，
 * 等于自己给服务端造一次小尖峰，也更容易再次一起失败。
 */
export function retryDelay (retryCount) {
  const base = Math.min(400 * Math.pow(2, retryCount - 1), 2000)
  return base + Math.floor(Math.random() * 200)
}

export function wait (ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * 网络错误提示的去重窗口。
 *
 * 全站 toast 是单例，后一条会直接盖掉前一条。弱网下一个页面首屏可能并发 5~6 个
 * 请求一起失败，不去重的话同一句「网络连接失败」会把用户刚看到的其它提示反复冲掉。
 * 同一类错误在窗口内只提示一次。
 */
const NOTIFY_DEDUPE_WINDOW = 3000
let lastNotifiedKey = ''
let lastNotifiedAt = 0

export function notifyNetworkError (classified) {
  if (!classified.message) return

  const now = Date.now()
  if (classified.kind === lastNotifiedKey && now - lastNotifiedAt < NOTIFY_DEDUPE_WINDOW) {
    return
  }
  lastNotifiedKey = classified.kind
  lastNotifiedAt = now

  const toast = typeof window !== 'undefined' && window.vm && window.vm.$toast
  if (!toast) {
    console.warn('[network]', classified.message)
    return
  }
  // 断网/超时是环境问题而非操作出错，用 warning 而不是 error 的红色
  const level = classified.kind === ERROR_KIND.SERVER ? 'error' : 'warning'
  toast[level](classified.message)
}

/**
 * 把归类结果挂到 error 上，供调用方判断（例如决定展示重试占位还是错误文案）。
 *
 * 页面里可以这样用：
 *   catch (e) { if (e.isNetworkError) this.showRetryPlaceholder = true }
 */
export function decorateError (error, classified) {
  if (!error || typeof error !== 'object') return error
  error.isNetworkError = isNetworkKind(classified.kind)
  error.networkErrorKind = classified.kind
  error.friendlyMessage = classified.message
  return error
}
